import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CopyToClipboard } from "../../Utils";
import { useEffect, useState } from "react";
import { BookOpenIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useUserHasDrivingFleetVehicleListAccess } from "../../../routes/roleOnly";
import { VehicleForm } from "./vehicle-form";
import { fetchDrivingTrips } from "../../../api/driving/trip";
import { InsuranceSection } from "./InsuranceSection";
import { DamageSection } from "./DamageSection";
import { ChecklistSection } from "./ChecklistSection";

export const VehicleInfo = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh } = props;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [lastTrip, setLastTrip] = useState(null);
  const canEditFleetVehicle = useUserHasDrivingFleetVehicleListAccess();

  useEffect(() => {
    async function loadLastTrip() {
      if (!item?.id) return;
      try {
        const response = await fetchDrivingTrips({
          filters: [{ name: "vehicle_id", value: item.id }],
          per_page: 1,
          page: 1,
          sortBy: "start_date",
          sort: "DESC",
        });
        const list = response?.data || [];
        setLastTrip(list.length > 0 ? list[0] : null);
      } catch (e) {
        setLastTrip(null);
      }
    }
    loadLastTrip();
  }, [item?.id]);

  return (
    <>
      {/* Basic Vehicle Information */}
      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="grid sm:grid-cols-8 gap-6">
              <div className="sm:col-span-2">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-700 mr-2">
                    {t("Active")}:
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      item?.active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {item?.active ? t("Yes") : t("No")}
                  </span>
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-700 mr-2">
                    {t("Norwegian")}:
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      item?.norwegian
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {item?.norwegian ? t("Yes") : t("No")}
                  </span>
                </div>
              </div>
            </div>
            {lastTrip && (
              <div className="sm:col-span-4">
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Last Ride")}
                  </span>
                  <div className="text-sm text-gray-900">
                    {lastTrip.end_address || lastTrip.start_address || "-"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(lastTrip.end_date || lastTrip.start_date).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {item?.id ? (
                <Link
                  to={`/dashboard/driving/vehicles/${item.id}/handbook`}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <BookOpenIcon className="w-4 h-4 mr-2" />
                  {t("Vehicle handbook")}
                </Link>
              ) : null}
              {canEditFleetVehicle ? (
                <button
                  type="button"
                  onClick={() => setEditModalOpen(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  {t("Edit Vehicle")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("Vehicle Information")}
            </h3>
            <p className="mt-1 text-sm text-gray-500"></p>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Number Plate")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.number_plate || "-"}
                  </p>
                  <CopyToClipboard value={item?.number_plate || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("VIN (Chassis Number)")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.vehicle_identification_number || "-"}
                  </p>
                  <CopyToClipboard
                    value={item?.vehicle_identification_number || ""}
                  />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Make")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.make || "-"}
                  </p>
                  <CopyToClipboard value={item?.make || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Model")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.model || "-"}
                  </p>
                  <CopyToClipboard value={item?.model || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Model Year")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.model_year || "-"}
                  </p>
                  <CopyToClipboard value={item?.model_year || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Body Style")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.body_style || "-"}
                  </p>
                  <CopyToClipboard value={item?.body_style || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Nickname")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.nickname || "-"}
                  </p>
                  <CopyToClipboard value={item?.nickname || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Current Odometer (km)")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.current_odometer_in_kilometers
                      ? `${item.current_odometer_in_kilometers.toLocaleString()} km`
                      : "-"}
                  </p>
                  <CopyToClipboard
                    value={
                      item?.current_odometer_in_kilometers?.toString() || ""
                    }
                  />
                </div>
              </div>
            </div>

            <div className="col-span-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Description")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.description || "-"}
                  </p>
                  <CopyToClipboard value={item?.description || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statens Vegvesen Information - Only show for Norwegian vehicles */}
      {item?.norwegian && (
        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {t("Statens Vegvesen Information")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Norwegian Vehicle Authority Information
              </p>
            </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Registration Date")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.registration_date || "-"}
                  </p>
                  <CopyToClipboard value={item?.registration_date || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("First Registration")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.first_registration || "-"}
                  </p>
                  <CopyToClipboard value={item?.first_registration || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Vehicle Type")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.vehicle_type || "-"}
                  </p>
                  <CopyToClipboard value={item?.vehicle_type || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Fuel Type")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.fuel_type || "-"}
                  </p>
                  <CopyToClipboard value={item?.fuel_type || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Engine Size (cc)")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.engine_size ? `${item.engine_size} cc` : "-"}
                  </p>
                  <CopyToClipboard
                    value={item?.engine_size?.toString() || ""}
                  />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Power (kW)")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.power_kw ? `${item.power_kw} kW` : "-"}
                  </p>
                  <CopyToClipboard value={item?.power_kw?.toString() || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Inspection Information - Only show for Norwegian vehicles */}
      {item?.norwegian && (
        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("Inspection Information")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              EU-kontroll / Periodic Vehicle Inspection
            </p>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Last Inspection Date")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.last_inspection_date || "-"}
                  </p>
                  <CopyToClipboard value={item?.last_inspection_date || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Next Inspection Date")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.next_inspection_date || "-"}
                  </p>
                  <CopyToClipboard value={item?.next_inspection_date || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Inspection Result")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.inspection_result || "-"}
                  </p>
                  <CopyToClipboard value={item?.inspection_result || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Inspection Station")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.inspection_station || "-"}
                  </p>
                  <CopyToClipboard value={item?.inspection_station || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Insurance Information */}
      <InsuranceSection vehicleId={item?.id} />

      {/* Service Information - Only show for Norwegian vehicles */}
      {item?.norwegian && (
        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("Service Information")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Maintenance and Service Details
            </p>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Last Service Date")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.last_service_date || "-"}
                  </p>
                  <CopyToClipboard value={item?.last_service_date || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Next Service Date")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.next_service_date || "-"}
                  </p>
                  <CopyToClipboard value={item?.next_service_date || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Service Interval (km)")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.service_interval_km
                      ? `${item.service_interval_km.toLocaleString()} km`
                      : "-"}
                  </p>
                  <CopyToClipboard
                    value={item?.service_interval_km?.toString() || ""}
                  />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Service Provider")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.service_provider || "-"}
                  </p>
                  <CopyToClipboard value={item?.service_provider || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Warranty End Date")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.warranty_end_date || "-"}
                  </p>
                  <CopyToClipboard value={item?.warranty_end_date || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Warranty Type")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.warranty_type || "-"}
                  </p>
                  <CopyToClipboard value={item?.warranty_type || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Damage Reports Section */}
      <DamageSection vehicleId={item?.id} />

      {/* Internal Controls Section */}
      <ChecklistSection vehicleId={item?.id} />

      {/* Edit Vehicle Dialog */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Vehicle
              </h3>
              <button
                onClick={() => setEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <VehicleForm
              vehicle={item}
              onClose={async (success) => {
                setEditModalOpen(false);
                if (success) {
                  // Refresh the vehicle data
                  if (onRefresh) {
                    await onRefresh();
                  }
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};
